import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SceneHead, routesFor, type Lang } from "../primitives";

const t = {
  es: {
    aria: "Metodología",
    label: "Metodología",
    title: "Primero entender. Después invertir.",
    intro:
      "Así se trabaja un proyecto contratado. No es lo que se resuelve en la sesión inicial gratuita de 30 minutos.",
    step: "Paso",
    cta: "Ver la metodología completa",
    steps: [
      {
        n: "1",
        name: "Contexto",
        text: "Objetivos de negocio, oferta, mercado y medición disponible hoy.",
        out: "Entregable: marco de trabajo y criterios de éxito acordados.",
      },
      {
        n: "2",
        name: "Investigación",
        text: "Demanda, intención de búsqueda y competencia, según el alcance contratado.",
        out: "Entregable: mapa de demanda e intención por tipo de página.",
      },
      {
        n: "3",
        name: "Prioridades",
        text: "Acciones ordenadas por impacto, esfuerzo y dependencias técnicas.",
        out: "Entregable: plan priorizado con responsables y secuencia.",
      },
      {
        n: "4",
        name: "Implementación",
        text: "Técnica, contenido, diseño o campañas, según lo definido en el proyecto.",
        out: "Entregable: cambios ejecutados o especificados para tu equipo.",
      },
      {
        n: "5",
        name: "Medición",
        text: "Oportunidades generadas, calidad del contacto y resultados disponibles.",
        out: "Entregable: lectura periódica y ajuste de prioridades.",
      },
    ],
  },
  en: {
    aria: "Methodology",
    label: "Methodology",
    title: "Understand first. Invest after.",
    intro:
      "This is how a contracted project runs. It is not what gets solved in the free 30-minute initial session.",
    step: "Step",
    cta: "See the full methodology",
    steps: [
      {
        n: "1",
        name: "Context",
        text: "Business goals, offer, market and the measurement available today.",
        out: "Deliverable: agreed working framework and success criteria.",
      },
      {
        n: "2",
        name: "Research",
        text: "Demand, search intent and competition, within the contracted scope.",
        out: "Deliverable: demand and intent map by page type.",
      },
      {
        n: "3",
        name: "Priorities",
        text: "Actions ordered by impact, effort and technical dependencies.",
        out: "Deliverable: prioritized plan with owners and sequence.",
      },
      {
        n: "4",
        name: "Implementation",
        text: "Technical work, content, design or campaigns, as defined in the project.",
        out: "Deliverable: changes executed or specified for your team.",
      },
      {
        n: "5",
        name: "Measurement",
        text: "Opportunities generated, contact quality and the results available.",
        out: "Deliverable: periodic reading and adjustment of priorities.",
      },
    ],
  },
} as const;

export const SceneMethod = ({ language = "es" as Lang }: { language?: Lang }) => {
  const listRef = useRef<HTMLOListElement>(null);
  const [seen, setSeen] = useState(0);
  const c = t[language];
  const routes = routesFor(language);

  // Progreso discreto según pasos visibles. Sin pinning ni altura ficticia.
  useEffect(() => {
    const items = listRef.current?.querySelectorAll<HTMLElement>("[data-step]");
    if (!items?.length || typeof IntersectionObserver === "undefined") return;
    const visible = new Set<number>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const i = Number((e.target as HTMLElement).dataset.step);
          if (e.isIntersecting) visible.add(i);
        });
        setSeen((prev) => Math.max(prev, visible.size ? Math.max(...visible) + 1 : 0));
      },
      { threshold: 0.45 },
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="v3-section bg-white" aria-label={c.aria} id="metodologia">
      <div className="v3-shell">
        <SceneHead index="04" label={c.label} title={c.title} intro={c.intro} />

        <div className="mt-10 flex items-center gap-4">
          <div className="v3-progress max-w-[16rem]" aria-hidden>
            <span style={{ width: `${(seen / c.steps.length) * 100}%` }} />
          </div>
          <p className="v3-micro">
            {Math.max(seen, 1)} / {c.steps.length}
          </p>
        </div>

        <ol ref={listRef} className="v3-steps mt-8 lg:mt-12">
          {c.steps.map((s, i) => (
            <Reveal as="li" key={s.n} delay={0.04 * i}>
              <div className="v3-step" data-step={i} data-active={i < seen ? "true" : undefined}>
                <span className="v3-step-dot" aria-hidden />
                <p className="v3-micro">
                  {c.step} {s.n}
                </p>
                <h3 className="mt-2 text-[1.15rem] font-semibold">{s.name}</h3>
                <p className="v3-body mt-3 text-[1rem]">{s.text}</p>
                <p className="v3-note mt-3 text-[0.85rem]">{s.out}</p>
              </div>
            </Reveal>
          ))}
        </ol>

        <Link to={routes.methodology} className="v3-inline-link mt-10">
          {c.cta}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
};
